import webBiLogger from '@wix/web-bi-logger';
import initSchemaLogger from '@wix/bi-logger-spettatore_bi';
import { mapTemplatesToData } from '../../BI/templatesToBiData';
import { Template } from '../../../types/api';
import { CategoriesStore } from './CategoriesStore';
import { RoutingStore } from './RoutingStore';
import { FilterKey, FilterCollection } from './routes/sortFiltersParams';
import { TemplatesStore, TemplateStoreFetchParams, WidgetType } from './TemplatesStore';
import { Interaction } from './Interaction';
import { HeaderStore } from './HeaderStore';
import { getFilterAndSortParameters, isForYouPageRouteLoaded } from './routes/route';
import { BaseURLStore } from './BaseURLStore';
import { ForYouPageStore } from './ForYouPageStore';

export const DOCS_PER_PAGE = 12;

export interface ViewTemplateBI {
  siteId: string;
  category: string | 'search' | null;
  subCategory: string | null;
  galleryDocIndex: number;
  openingBrowserUrl: string;
  hasVideo: boolean;
  origin: 'create-page' | 'thumbnail' | 'video_preview';
}

export interface widgetHoverParams {
  hoverTime: number;
  widgetType: WidgetType;
}

export interface EditTemplateBI {
  siteId: string;
  galleryDocIndex: number;
  openingBrowserUrl: string;
  metaSiteId: string;
  editorSessionId: string;
  hasVideo: boolean;
  origin: 'create-page' | 'video_preview';
  category: string | 'search' | null;
  subCategory: string | null;
}

export interface TemplateVideoPreviewOpenedBI {
  siteId: string;
  category: string | 'search' | null;
  subCategory: string | null;
}

export interface TemplateVideoPreviewClosedBI {
  siteId: string;
  category: string | 'search' | null;
  subCategory: string | null;
  videoFullDuration?: number;
  videoPlaybackPosition?: number;
  videoStartTime?: number;
  videoEndTime?: number;
  videoStatus?: 'have_nothing' | 'have_metadata' | 'have_current_data' | 'have_future_data' | 'have_enough_data';
}

export type InteractionType = 'search' | 'pagination' | 'category_switch' | 'for-you-page';

interface SubCategoryBI {
  selected_value: string;
  previous_value: string;
}

type TemplatesFilterPaneAction = 'open' | 'close' | 'select' | 'unselect' | 'clear' | 'hover' | 'show' | 'hide';
type FilterElements = 'all' | 'sort';

interface LogFilterParams {
  type: keyof FilterCollection | FilterElements;
  previousValues: FilterCollection;
  selectedValues?: FilterCollection;
  clickedItemValue?: FilterKey;
}

interface TemplatesFilterPaneParams extends LogFilterParams {
  action: TemplatesFilterPaneAction;
  selectedValues: FilterCollection;
}

interface ClickOnElementsBI {
  clicked_element: string;
  clicked_element_identifier: string;
  galleryDocIndex?: number;
  clickedTemplateHasVideo?: boolean;
  openingBrowserUrl?: string;
  category: string | 'search' | null;
  subCategory: string | null;
}

export interface LogSearchParams {
  criteria: string;
  totalPages: number;
  currentPage: number;
  numberOfTemplatesInCurrentPage: number;
  source: string;
  searchId: string;
}

export interface LogTemplateHoverParams {
  galleryDocIndex: number;
  duration: number;
  product: string;
  templateId: string;
  hasVideo: boolean;
  siteId: string;
  category: string | 'search' | null;
  subCategory: string | null;
}

interface InitParams {
  routingStore: RoutingStore;
  categoriesStore: CategoriesStore;
  templatePageSessionId: string;
  templatesStore: TemplatesStore;
  headerStore: HeaderStore;
  forYouPageStore: ForYouPageStore;
  baseURLStore: BaseURLStore;
}

interface AboveTheFoldImpressionBI {
  galleryDocIndex: number;
  template_id: string;
}

export type WidgetClickedElement =
  | 'Thumbnail'
  | 'Main Title'
  | 'Select Tool'
  | 'Tool Title'
  | 'Tool Image'
  | 'Tool Pagination'
  | 'Tool Description'
  | 'Slider Dot'
  | 'Backward Arrow'
  | 'Forward Arrow'
  | 'Back Button';

export type screenType = 'main' | 'forYou' | 'search' | 'category';

export class UiLoggerStore {
  private routingStore: RoutingStore;
  private categoriesStore: CategoriesStore;
  private headerStore: HeaderStore;
  private baseURLStore: BaseURLStore;
  private forYouPageStore: ForYouPageStore;
  private tpsi: string;
  private templatesStore: TemplatesStore;
  private interactions: Map<string, Interaction> = new Map();
  private lastInteractionKey: string = null;

  logger = initSchemaLogger(webBiLogger)();

  init({
    routingStore,
    categoriesStore,
    templatesStore,
    headerStore,
    baseURLStore,
    forYouPageStore,
    templatePageSessionId,
  }: InitParams) {
    this.routingStore = routingStore;
    this.categoriesStore = categoriesStore;
    this.templatesStore = templatesStore;
    this.headerStore = headerStore;
    this.baseURLStore = baseURLStore;
    this.forYouPageStore = forYouPageStore;
    this.tpsi = templatePageSessionId;
  }

  public initInteraction(interactionType: InteractionType, startTime?: number) {
    const interaction = new Interaction(interactionType, startTime);
    this.lastInteractionKey = interaction.key;
    this.interactions.set(interaction.key, interaction);
    return interaction;
  }

  public getLastInteractionKey() {
    return this.lastInteractionKey;
  }

  private eraseLastInteractionKey() {
    this.lastInteractionKey = null;
  }

  public get templatePageSessionId(): string {
    return this.tpsi;
  }

  private getWindowParams() {
    return typeof window !== 'object'
      ? {}
      : {
          window_width: window.innerWidth,
          window_height: window.innerHeight,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          avail_width: window.screen.availWidth,
          avail_height: window.screen.availHeight,
        };
  }

  private getImplicitFields() {
    return {
      page_index: this.routingStore.currentPage,
    };
  }

  private getCategoryAndSubCategory(fetchParams: TemplateStoreFetchParams) {
    const isSearch = fetchParams?.type === 'search';
    const isForYouPage = isForYouPageRouteLoaded(
      this.routingStore.matchedRoute,
      this.forYouPageStore.isLoaded,
      this.templatesStore.isLoaded,
    );

    return {
      category: isForYouPage ? undefined : isSearch ? 'search' : fetchParams?.categorySlug,
      subcategory: isForYouPage || isSearch ? undefined : fetchParams?.subCategorySlug ?? '',
    };
  }

  private get screenType(): screenType {
    const { matchedRoute } = this.routingStore;
    const isForYouPageLoaded = isForYouPageRouteLoaded(
      matchedRoute,
      this.forYouPageStore.isLoaded,
      this.templatesStore.isLoaded,
    );

    if (isForYouPageLoaded) {
      return 'forYou';
    } else if (this.templatesStore.loadedFetchParams?.criteria) {
      return 'search';
    } else if (this.templatesStore.loadedFetchParams?.categorySlug === 'all') {
      return 'main';
    } else if (this.templatesStore.loadedFetchParams?.type === 'category') {
      return 'category';
    }
    return null;
  }

  private templatesFilterPane({
    type,
    selectedValues,
    previousValues,
    action,
    clickedItemValue,
  }: TemplatesFilterPaneParams) {
    void this.logger.templatesPageGalleryFilterPane({
      action,
      menu_name: type,
      book: this.categoriesStore.bookName,
      tpsi: this.templatePageSessionId,
      updated_values: JSON.stringify(selectedValues),
      previous_values: JSON.stringify(previousValues),
      updated_features: selectedValues.features.join(','),
      updated_layouts: selectedValues.layouts.join(','),
      updated_colors: selectedValues.colors.join(','),
      updated_styles: selectedValues.colorStyles.join(','),
      clicked_value: clickedItemValue ?? '',
      ...this.getCategoryAndSubCategory(this.templatesStore.fetchParams),
      ...this.getImplicitFields(),
    });
  }

  public logSearch(params: LogSearchParams) {
    const { matchedRoute, previousMatchedRoute } = this.routingStore;

    if (matchedRoute.routeName === 'search') {
      let previousCategory = '';
      let previousSubCategory = '';

      if (previousMatchedRoute) {
        if (previousMatchedRoute.routeName === 'category' || previousMatchedRoute.routeName === 'subCategory') {
          previousCategory = previousMatchedRoute.params.categorySlug;
        }

        if (previousMatchedRoute.routeName === 'subCategory') {
          previousSubCategory = previousMatchedRoute.params.subCategorySlug;
        }
      }

      void this.logger.templatesSearch({
        click_source: this.headerStore.isSticky ? 'Sticky Header' : 'Main Header',
        book: this.categoriesStore.bookName,
        tpsi: this.templatePageSessionId,
        source: params.source,
        previous_category: previousCategory,
        previous_sub_category: previousSubCategory,
        currentPage: params.currentPage,
        totalPages: params.totalPages,
        criteria: params.criteria,
        numberOfTemplatesInCurrentPage: params.numberOfTemplatesInCurrentPage,
        search_guid: params.searchId,
        ...getFilterAndSortParameters(this.templatesStore.fetchParams),
      });
    }
  }

  public logCloseFiltersElement(params: LogFilterParams) {
    this.templatesFilterPane({
      action: 'close',
      selectedValues: params.previousValues,
      ...params,
    });
  }

  public logOpenFiltersElement(params: LogFilterParams) {
    this.templatesFilterPane({
      action: 'open',
      selectedValues: params.previousValues,
      ...params,
    });
  }

  public logShowFiltersPanel(params: LogFilterParams) {
    this.templatesFilterPane({
      action: 'show',
      selectedValues: params.previousValues,
      ...params,
    });
  }

  public logHideFiltersPanel(params: LogFilterParams) {
    this.templatesFilterPane({
      action: 'hide',
      selectedValues: params.previousValues,
      ...params,
    });
  }

  public logHoverFilter(params: LogFilterParams) {
    this.templatesFilterPane({
      action: 'hover',
      selectedValues: params.previousValues,
      ...params,
    });
  }

  public logSelectFilter(params: LogFilterParams) {
    this.templatesFilterPane({
      action: 'select',
      selectedValues: params.selectedValues,
      ...params,
    });
  }

  public logUnselectFilter(params: LogFilterParams) {
    this.templatesFilterPane({
      action: 'unselect',
      selectedValues: params.selectedValues,
      ...params,
    });
  }

  public logClearFilters(params: LogFilterParams) {
    this.templatesFilterPane({
      action: 'clear',
      selectedValues: params.selectedValues,
      ...params,
    });
  }

  public logMainCategoryMenuOpen(category_group: string) {
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.fetchParams);
    void this.logger.templatesPageMainCategoryMenu({
      click_source: this.headerStore.isSticky ? 'Sticky Header' : 'Main Header',
      action: 'open',
      book: this.categoriesStore.bookName,
      category_group,
      tpsi: this.templatePageSessionId,
      category,
      sub_category: subcategory,
      ...this.getImplicitFields(),
    });
  }

  public logMainCategoryMenuClose(category_group: string) {
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.fetchParams);
    void this.logger.templatesPageMainCategoryMenu({
      click_source: this.headerStore.isSticky ? 'Sticky Header' : 'Main Header',
      action: 'close',
      book: this.categoriesStore.bookName,
      category_group,
      category,
      sub_category: subcategory,
      tpsi: this.templatePageSessionId,
      ...this.getImplicitFields(),
    });
  }

  public logMainCategoryMenuSelect(category_group: string, selected_category: string, selected_sub_category?: string) {
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.fetchParams);
    void this.logger.templatesPageMainCategoryMenu({
      click_source: this.headerStore.isSticky ? 'Sticky Header' : 'Main Header',
      action: 'select',
      book: this.categoriesStore.bookName,
      category_group,
      selected_value: selected_category,
      selected_sub_category,
      category,
      sub_category: subcategory,
      tpsi: this.templatePageSessionId,
      ...this.getImplicitFields(),
    });
  }

  public logSubCategoryAdd(props: SubCategoryBI) {
    void this.logger.templatesPageSubCategoryFilter({
      action: 'add',
      book: this.categoriesStore.bookName,
      tpsi: this.templatePageSessionId,
      category: this.getCategoryAndSubCategory(this.templatesStore.fetchParams).category,
      ...props,
      ...this.getImplicitFields(),
    });
  }

  public logSubCategoryRemove(props: SubCategoryBI) {
    void this.logger.templatesPageSubCategoryFilter({
      action: 'remove',
      book: this.categoriesStore.bookName,
      tpsi: this.templatePageSessionId,
      category: this.getCategoryAndSubCategory(this.templatesStore.fetchParams).category,
      ...props,
      ...this.getImplicitFields(),
    });
  }

  public logSubCategoryClear(props: SubCategoryBI) {
    void this.logger.templatesPageSubCategoryFilter({
      action: 'clear',
      book: this.categoriesStore.bookName,
      tpsi: this.templatePageSessionId,
      category: this.getCategoryAndSubCategory(this.templatesStore.fetchParams).category,
      ...props,
      ...this.getImplicitFields(),
    });
  }

  public logAboveTheFoldImpression(props: AboveTheFoldImpressionBI) {
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.loadedFetchParams);

    void this.logger.templatesTemplateAboveTheFoldImpression({
      book: this.categoriesStore.bookName,
      sub_category: subcategory,
      category,
      galleryDocsPerPage: DOCS_PER_PAGE,
      siteType: 2,
      template_id: props.template_id,
      galleryDocIndex: props.galleryDocIndex,
      tpsi: this.templatePageSessionId,
      ...this.getWindowParams(),
      ...getFilterAndSortParameters(this.templatesStore.loadedFetchParams),
      ...this.getImplicitFields(),
    });
  }

  public logPagination(page: number) {
    void this.logger.templatesPagination({
      pagination_type: 'button',
      page_clicked_index: page,
      tpsi: this.templatePageSessionId,
      category: this.getCategoryAndSubCategory(this.templatesStore.fetchParams).category,
      ...this.getImplicitFields(),
    });
  }

  public logStartedLoadingThePage(key: string) {
    const interaction = this.interactions.get(key);
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.fetchParams);
    const isForYouPageLoaded = isForYouPageRouteLoaded(
      this.routingStore.matchedRoute,
      this.forYouPageStore.isLoaded,
      this.templatesStore.isLoaded,
    );

    if (interaction) {
      interaction.reportLoadingStart();
      void this.logger.templatesStartedLoadingThePage({
        book: this.categoriesStore.bookName,
        sub_category: subcategory,
        category,
        galleryDocsPerPage: isForYouPageLoaded ? this.forYouPageStore.pageTemplates.length : DOCS_PER_PAGE,
        interaction_type: interaction.type,
        platform: 'web',
        product: 'html',
        tpsi: this.templatePageSessionId,
        ...getFilterAndSortParameters(this.templatesStore.fetchParams),
        ...this.getImplicitFields(),
      });
    }
  }

  public logFinishedLoadingThePage(key: string) {
    let templates: Template[], galleryDocsPerPage;
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.fetchParams);
    const interaction = this.interactions.get(key);
    const isForYouPageLoaded = isForYouPageRouteLoaded(
      this.routingStore.matchedRoute,
      this.forYouPageStore.isLoaded,
      this.templatesStore.isLoaded,
    );

    if (interaction) {
      if (isForYouPageLoaded) {
        templates = this.forYouPageStore.pageTemplates;
        galleryDocsPerPage = templates.length;
      } else {
        const { itemsPerPage } = this.templatesStore;
        const templateMapValues = Array.from(itemsPerPage.values() ?? []);
        templates = [].concat(...Array.from(templateMapValues.length === 0 ? [] : templateMapValues[0]));
        galleryDocsPerPage = DOCS_PER_PAGE;
      }
      void this.logger.templatesFinishedLoadingThePage({
        book: this.categoriesStore.bookName,
        sub_category: subcategory,
        category,
        event_data: mapTemplatesToData(templates).toString(),
        galleryDocsPerPage,
        interaction_type: interaction.type,
        loadingTime: Date.now() - interaction.startTime,
        platform: 'web',
        product: 'html',
        tpsi: this.templatePageSessionId,
        ...getFilterAndSortParameters(this.templatesStore.fetchParams),
        ...this.getImplicitFields(),
      });
    }
  }

  public logPageFullyRendered(key: string) {
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.loadedFetchParams);
    const interaction = this.interactions.get(key);
    if (!interaction) {
      return;
    }

    this.interactions.delete(key);
    if (key === this.getLastInteractionKey()) {
      this.eraseLastInteractionKey();
    }

    const { itemsPerPage } = this.templatesStore;
    const templateMapValues = Array.from(itemsPerPage.values() ?? []);
    const templates: Template[] = [].concat(...Array.from(templateMapValues.length === 0 ? [] : templateMapValues[0]));

    void this.logger.templatesPagePageFullyRendered({
      book: this.categoriesStore.bookName,
      sub_category: subcategory,
      category,
      event_data: mapTemplatesToData(templates).toString(),
      gallerydocsperpage: DOCS_PER_PAGE,
      interaction_type: interaction.type,
      loadingTime: Date.now() - interaction.startTime,
      tpsi: this.templatePageSessionId,
      ...getFilterAndSortParameters(this.templatesStore.loadedFetchParams),
      ...this.getImplicitFields(),
    });
  }

  public logClickElement(params: ClickOnElementsBI) {
    const {
      galleryDocIndex,
      openingBrowserUrl,
      clicked_element,
      clickedTemplateHasVideo,
      category,
      subCategory,
      clicked_element_identifier,
    } = params;

    void this.logger.templatesClickOnElement({
      book: this.categoriesStore.bookName,
      sub_category: subCategory ?? '',
      category: category ?? '',
      clicked_element,
      clicked_element_identifier,
      galleryDocIndex,
      gallerydocsperpage: DOCS_PER_PAGE,
      videoThumbnail: clickedTemplateHasVideo,
      openingBrowserUrl,
      screen_type: this.screenType,
      tpsi: this.templatePageSessionId,
      ...this.getImplicitFields(),
    });
  }

  public logViewTemplate(params: ViewTemplateBI) {
    const isSearch = this.templatesStore.loadedFetchParams?.type === 'search';
    const { galleryDocIndex, openingBrowserUrl, siteId, hasVideo, category, subCategory, origin } = params;

    void this.logger.templatesViewTemplate({
      openingSiteID: siteId,
      openingBrowserUrl,
      openingbrowserurl: openingBrowserUrl,
      index: galleryDocIndex,
      book: this.categoriesStore.bookName,
      sub_category: subCategory ?? '',
      category: category ?? '',
      btn: true,
      is_search_result: isSearch,
      search_guid: this.templatesStore.searchId ?? '',
      siteType: 2,
      total_search_results: this.templatesStore.itemsTotal,
      tpsi: this.templatePageSessionId,
      origin,
      videoThumbnail: hasVideo,
      screen_type: this.screenType,
      ...getFilterAndSortParameters(this.templatesStore.loadedFetchParams),
      ...this.getWindowParams(),
    });
  }

  public logEditTemplate(params: EditTemplateBI) {
    const isSearch = this.templatesStore.loadedFetchParams?.type === 'search';
    const {
      galleryDocIndex,
      openingBrowserUrl,
      metaSiteId,
      editorSessionId,
      siteId,
      hasVideo,
      category,
      subCategory,
      origin,
    } = params;

    void this.logger.templatesOpenEditor({
      openingSiteID: siteId,
      openingBrowserUrl,
      index: galleryDocIndex,
      metasiteid: metaSiteId,
      esi: editorSessionId,
      book: this.categoriesStore.bookName,
      sub_category: subCategory ?? '',
      category: category ?? '',
      btn: true,
      is_search_result: isSearch,
      search_guid: this.templatesStore.searchId ?? '',
      siteType: 2,
      total_search_results: this.templatesStore.itemsTotal,
      tpsi: this.templatePageSessionId,
      origin,
      template_type: 'editor',
      videoThumbnail: hasVideo,
      screen_type: this.screenType,
      ...getFilterAndSortParameters(this.templatesStore.loadedFetchParams),
      ...this.getWindowParams(),
    });
  }

  public logTemplateInfoShown() {
    void this.logger.templatesClickedDescription({
      galleryDocsPerPage: this.templatesStore.loadedItemsCount,
      tpsi: this.templatePageSessionId,
    });
  }

  public logTemplateInfoHidden() {
    void this.logger.templatesClosedDescription({
      galleryDocsPerPage: this.templatesStore.loadedItemsCount,
      tpsi: this.templatePageSessionId,
    });
  }

  public logTemplateHover(params: LogTemplateHoverParams) {
    const { galleryDocIndex, duration, product, templateId, siteId, hasVideo, category, subCategory } = params;

    void this.logger.templatesHoverOnTemplate({
      openingSiteID: siteId,
      template_id: templateId,
      sub_category: subCategory ?? '',
      category: category ?? '',
      siteType: 2,
      product,
      origin: '',
      btn: false,
      hover_ms: duration,
      book: this.categoriesStore.bookName,
      index: galleryDocIndex,
      galleryDocIndex,
      galleryDocsPerPage: this.templatesStore.loadedItemsCount,
      videoThumbnail: hasVideo,
      tpsi: this.templatePageSessionId,
      screen_type: this.screenType,
      ...this.getWindowParams(),
      ...getFilterAndSortParameters(this.templatesStore.loadedFetchParams),
      ...this.getImplicitFields(),
    });
  }

  public logTemplateVideoPreviewOpened(params: TemplateVideoPreviewOpenedBI) {
    const isSearch = this.templatesStore.loadedFetchParams?.type === 'search';
    const { category, subCategory, siteId } = params;

    void this.logger.templatesVideoPreviewOpen({
      openingSiteID: siteId,
      book: this.categoriesStore.bookName,
      sub_category: subCategory ?? '',
      category: category ?? '',
      is_search_result: isSearch,
      search_guid: this.templatesStore.searchId ?? '',
      total_search_results: this.templatesStore.itemsTotal,
      tpsi: this.templatePageSessionId,
      screen_type: this.screenType,
      ...getFilterAndSortParameters(this.templatesStore.loadedFetchParams),
      ...this.getWindowParams(),
    });
  }

  public logTemplateVideoPreviewClosed(params: TemplateVideoPreviewClosedBI) {
    const isSearch = this.templatesStore.loadedFetchParams?.type === 'search';
    const {
      category,
      subCategory,
      videoFullDuration,
      videoPlaybackPosition,
      videoStartTime,
      videoEndTime,
      videoStatus,
      siteId,
    } = params;

    void this.logger.templatesCloseVideoPreview({
      openingSiteID: siteId,
      book: this.categoriesStore.bookName,
      sub_category: subCategory ?? '',
      category: category ?? '',
      is_search_result: isSearch,
      search_guid: this.templatesStore.searchId ?? '',
      total_search_results: this.templatesStore.itemsTotal,
      tpsi: this.templatePageSessionId,
      video_full_duration: videoFullDuration,
      video_playback_position: videoPlaybackPosition,
      video_start_time: videoStartTime,
      video_end_time: videoEndTime,
      video_status: videoStatus,
      screen_type: this.screenType,
      ...getFilterAndSortParameters(this.templatesStore.loadedFetchParams),
      ...this.getWindowParams(),
    });
  }

  public logWidgetAboveTheFoldImpression() {
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.loadedFetchParams);

    void this.logger.templatesWidgetAboveTheFoldImpression({
      widget_name: this.templatesStore.getWidgetType(this.templatesStore.loadedFetchParams) ?? '',
      book: this.categoriesStore.bookName,
      category,
      sub_category: subcategory,
      tpsi: this.templatePageSessionId,
      ...this.getWindowParams(),
    });
  }

  public logWidgetLoaded(widgetType: WidgetType) {
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.loadedFetchParams);

    void this.logger.templatesWidgetLoaded({
      category,
      sub_category: subcategory,
      widget_name: widgetType,
      tpsi: this.templatePageSessionId,
      ...this.getWindowParams(),
    });
  }

  public logWidgetHover({ widgetType, hoverTime }: widgetHoverParams) {
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.loadedFetchParams);

    void this.logger.templatesHoverOnWidget({
      hover_ms: hoverTime,
      category,
      sub_category: subcategory,
      widget_name: widgetType,
      tpsi: this.templatePageSessionId,
      ...this.getWindowParams(),
    });
  }

  public logWidgetActions(clicked_element: WidgetClickedElement, tool_index?: number, tool_index_selected?: number) {
    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.loadedFetchParams);

    void this.logger.templatesWidgetActions({
      clicked_element,
      tool_index,
      tool_index_selected,
      widget_name: this.templatesStore.getWidgetType(this.templatesStore.loadedFetchParams) ?? '',
      book: this.categoriesStore.bookName,
      category,
      sub_category: subcategory,
      tpsi: this.templatePageSessionId,
      ...this.getWindowParams(),
    });
  }

  public logGenericPageView = () => {
    let templates: Template[];
    let gallerydocsperpage: number;

    const { category, subcategory } = this.getCategoryAndSubCategory(this.templatesStore.fetchParams);
    const {
      previousLocation,
      matchedRoute: { routeName },
    } = this.routingStore;
    const isSearch = routeName === 'search';
    const { params } = this.routingStore.matchedRoute;
    const criteria = 'criteria' in params ? params.criteria : '';
    const isForYouPageLoaded = isForYouPageRouteLoaded(
      this.routingStore.matchedRoute,
      this.forYouPageStore.isLoaded,
      this.templatesStore.isLoaded,
    );

    if (isForYouPageLoaded) {
      templates = this.forYouPageStore.pageTemplates;
      gallerydocsperpage = templates.length;
    } else {
      const templateMapValues = Array.from(this.templatesStore.itemsPerPage?.values() ?? []);
      templates = [].concat(...Array.from(templateMapValues.length === 0 ? [] : templateMapValues[0]));
      gallerydocsperpage = DOCS_PER_PAGE;
    }

    void this.logger.templatesGenericPageView({
      gallerydocsperpage,
      is_search_result: isSearch,
      search_guid: this.templatesStore.searchId ?? '',
      sub_category: subcategory,
      category,
      screen_type: this.screenType,
      criteria,
      previous_url: previousLocation ? this.baseURLStore.buildURL(previousLocation) : document.referrer,
      eventData: mapTemplatesToData(templates).toString(),
      total_search_results: this.templatesStore.itemsTotal,
      tpsi: this.templatePageSessionId,
      ...this.getWindowParams(),
      ...getFilterAndSortParameters(this.templatesStore.fetchParams),
    });
  };
}
